import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import {
  Header,
  Helmet,
  ThemeLayout,
  Content,
  Footer,
  Intro,
} from '@components';
import { ScrollToTop, Breadcrumb } from 'ech-components/src/components';
import { injectIntl, IntlShape, WrappedComponentProps } from 'react-intl';
import {
  Grid,
  GridItem,
  Spacer,
  Container,
  Main,
} from 'ech-components/src/helpers';
import { H1 } from 'ech-components/src/primitives';
import { options } from '@components/Content';
import { createLanguagePath, createRoutingURI } from '@scripts/utils/domain';
import { getRootTranslation } from '../../gatsby-helper';

const StyledTitle = styled(H1)``;

const ToolHubTemplate = ({ data, intl, pageContext }: ToolHubTemplateProps) => {
  const { contentfulToolHub, contentfulCampaignConfiguration } = data;

  if (!contentfulToolHub) return <>slug not found</>;

  const {
    seoTitle,
    seoDescription,
    navigationTitle,
    title,
    content,
    leadText,
    leadImage,
  } = contentfulToolHub;
  const { campaignKey } = contentfulCampaignConfiguration;

  const pageUrl = createLanguagePath({
    switcher: pageContext.lang.switcher,
    language: intl.locale,
  });

  const breadcrumbs = [
    {
      title: intl.formatMessage({ id: 'page.navigation.tools' }),
      href: createRoutingURI({
        root: getRootTranslation('tools', intl.locale),
        slug: '',
        locale: intl.locale,
      }),
    },
    {
      title: navigationTitle,
      href: pageUrl,
    },
  ];

  return (
    <ThemeLayout
      customTheme={{
        campaign: {
          key: campaignKey,
        },
      }}
    >
      <Helmet
        locale={pageContext.lang.actual}
        langObj={pageContext.lang}
        description={seoDescription}
        title={seoTitle}
        image={leadImage ? leadImage : null}
        matomo={pageContext.matomo}
      />
      <Header langObj={pageContext.lang} />
      <Main>
        <Spacer space="giga" />
        <Container>
          <Grid columns={12}>
            <GridItem
              size={{ zero: 12, medium: 10, large: 8 }}
              push={{ zero: 0, medium: 1, large: 2 }}
            >
              <Breadcrumb items={breadcrumbs} />
              <StyledTitle>{title}</StyledTitle>
              <Spacer space="gamma" />
            </GridItem>
          </Grid>
          <Intro leadText={leadText} leadImage={leadImage} pageUrl={pageUrl} />
          {content ? (
            <section id="content">
              <Content content={content} options={options} />
            </section>
          ) : null}
        </Container>
        <Spacer space="peta" />
        <ScrollToTop
          backToTopText={intl.formatMessage({
            id: 'page.backToTop',
          })}
        />
        <Spacer space="peta" />
      </Main>
      <Footer langObj={pageContext.lang} />
    </ThemeLayout>
  );
};

export const query = graphql`
  query ContentfulToolHubTemplate(
    $slug: String
    $locale: String
    $campaignKey: String
  ) {
    contentfulCampaignConfiguration(
      node_locale: { eq: $locale }
      campaignKey: { eq: $campaignKey }
    ) {
      campaignName
      campaignKey
    }
    contentfulToolHub(slug: { eq: $slug }, node_locale: { eq: $locale }) {
      __typename
      node_locale
      seoDescription
      seoTitle
      navigationTitle
      title
      slug
      leadText {
        raw
      }
      leadImage {
        ...image1260X480
        ...imageOpenGraph
      }
      content {
        ...contentfulContentToolHubFragment
      }
    }
  }
`;

export type ToolHubTemplateProps = {
  data: any;
  intl: IntlShape;
  pageContext: any;
} & WrappedComponentProps;

export default injectIntl(ToolHubTemplate);
